import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./Modal.css";
import "./GeoCharts.css";
import { countries, countries_continents, continents } from "../../Data/Countries";
import { citiesByCountry } from "../../Data/Cities";
import Section from "../../Models/Section";
import InnerHTML from "dangerously-set-html-content";
import Place from "../../Models/Place";
import PlaceMenu from "../../CommonWeb/PlaceMenu";
import AlertModal from "./AlertModal";
import MenuModal from "./MenuModal";
import PlaceEditModal from "./PlaceEditModal";
import HTMLReader from "./HTMLReader";
import { BarChart } from "react-gifted-charts";
import { DateTime } from "luxon";
import { useDispatch } from "react-redux";
import { slice1Actions } from "../../store/reducer";
import DataBase from "../../dataBase/liquidGuides";
import { buildGuideSummary, mergeWanderlist, placeKey, isCurrentSummary } from "../../Common/wanderlistSummary";
import { demoWanderlistUid } from "../../Common/featureFlags";

const GeoCharts = (props) => {
  const user_info = useSelector((state) => state.user_info);
  const guide_meta = useSelector((state) => state.guide_meta);
  // The user's other guides' Wanderlist summaries (2026-10-23, matches
  // mobile): [{ guideId, guideName, summary }], read each time Wanderlist
  // opens. Read only — nothing is copied into the current guide.
  const [otherGuides, setOtherGuides] = useState([]);
  const [isSample, setIsSample] = useState(false);
  // const [placesToVisit, SetplacesToVisit] = useState([]);
  // const [SectionHTML, SetSectionHTML] = useState("");
  const [regionSelected, setregionSelected] = useState("");
  const [citiesVisited, setcitiesVisited] = useState("");
  const [continentSelected, setContinentSelected] = useState("");
  const [addCityShow, setAddCityShow] = useState(false);
  const [addCityName, setAddCityName] = useState("");
  const [addCityCountry, setAddCityCountry] = useState([...countries].sort()[0] || "");
  // "select" shows a dropdown of the country's curated cities (when any
  // exist); "type" shows the original free-text input — reached via the
  // dropdown's own "Type a different city…" option, or automatically for a
  // country with no curated cities.
  const [addCityMode, setAddCityMode] = useState("select");
  const [addCityVisited, setAddCityVisited] = useState(false);
  const [addCityBucketList, setAddCityBucketList] = useState(false);
  const [addCityError, setAddCityError] = useState("");
  const [addCitySaving, setAddCitySaving] = useState(false);
  const [overrideStats, setOverrideStats] = useState(null);
  const stats = overrideStats ?? (Array.isArray(props.data) ? props.data : []);
  const [chartDataMode, setChartDataMode] = useState("places");
  const [chartMode, setChartMode] = useState("year");
  const [chartFilter, setChartFilter] = useState("visited");
  const [alertShow, setAlertShow] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertButtons, setAlertButtons] = useState([]);
  const [cityMenuShow, setCityMenuShow] = useState(false);
  const [cityMenuList, setCityMenuList] = useState([]);
  const [cityMenuHeading, setCityMenuHeading] = useState("");
  const [htmlReaderShow, setHtmlReaderShow] = useState(false);
  const [htmlReaderContent, setHtmlReaderContent] = useState("");
  const [editPlaceShow, setEditPlaceShow] = useState(false);
  const [editPlaceTarget, setEditPlaceTarget] = useState(null);

  const dispatch = useDispatch();

  // The current guide's real places plus the other guides' visited/bucket-
  // listed places that aren't already in it (duplicates removed by
  // placeKey). Other guides' places are read-only stand-ins (_external).
  const allPlaces = () => {
    const own = window.guide_obj?.places?.placesList || [];
    const ownKeys = new Set(own.map(placeKey));
    const external = mergeWanderlist(otherGuides).places
      .filter((p) => !ownKeys.has(placeKey(p)))
      .map((p) => ({
        ID: `ext:${placeKey(p)}`, Name: p.name, City: p.city, Country: p.country, Lat: p.lat, Longi: p.lng,
        Visited: !!p.visited, BucketList: !!p.bucket, _external: true, _guides: p.guides,
      }));
    return own.concat(external);
  };
  // Cities visited with their continent/region, from every guide (same rule
  // as Guide.getGuideStatistics: a visited place with a city and a known
  // country; one entry per city).
  const visitedStats = () => {
    const out = [];
    const seen = new Set();
    for (const p of allPlaces()) {
      if (!p.Visited || !p.Country || !p.City) continue;
      const key = `${p.City}::${p.Country}`;
      if (seen.has(key)) continue;
      const cc = countries_continents.find((c) => c.country === p.Country);
      if (!cc) continue;
      seen.add(key);
      out.push({ Continent: cc.continent, Country: p.Country, Region: cc.region || "", State: "", City: p.City });
    }
    return out;
  };

  const refreshStats = () => setOverrideStats(visitedStats());

  useEffect(() => {
    if (props.show && window.guide_obj) {
      setOverrideStats(visitedStats());
    }
  }, [props.show, otherGuides]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every own guide's summary (Library "Mine"), when Wanderlist opens; guides
  // saved before summaries existed get theirs once, in the background.
  useEffect(() => {
    if (!props.show) return;
    const own = user_info?.uid;
    // A guest sees the demo account's guides as a sample (read only).
    const sample = !own || own === "guest";
    const uid = sample ? demoWanderlistUid() : own;
    setIsSample(sample);
    let cancelled = false;
    DataBase.getAllGuideInfo(uid, async (res) => {
      if (cancelled || !res || res.pass !== true || !Array.isArray(res.res)) return;
      const current = String(guide_meta?.guide_info_id || "");
      // Featured guides aren't the user's own travels, even when they own
      // them (the admin owns hundreds) — left out, and never filled.
      const mine = res.res.filter((g) => String(g.id) !== current && g.data?.user_id === uid && g.data?.type !== "Featured");
      const toSource = (g, summary) => ({ guideId: g.id, guideName: g.data.guide_name || "Untitled", summary });
      setOtherGuides(mine.filter((g) => isCurrentSummary(g.data?.wanderlist)).map((g) => toSource(g, g.data.wanderlist)));
      if (sample) return;   // never write to someone else's guides
      for (const g of mine.filter((x) => !isCurrentSummary(x.data?.wanderlist) && x.data?.guide_id)) {
        const summary = await DataBase.fillGuideWanderlist(g.id, g.data.guide_id);
        if (cancelled) return;
        if (summary) setOtherGuides((prev) => [...prev.filter((o) => o.guideId !== g.id), toSource(g, summary)]);
      }
    }, "Mine");
    return () => { cancelled = true; };
  }, [props.show, user_info?.uid, guide_meta?.guide_info_id]);

  // This guide, read live, as one more source next to the other guides.
  const currentSource = () => ({ guideId: String(guide_meta?.guide_info_id || "current"), guideName: window.guide_obj?.attributes?.guide_name || "This guide", summary: window.guide_obj ? buildGuideSummary(window.guide_obj) : null });

  // A place or trip from another of the user's guides: Wanderlist only reads
  // other guides, so it says which guide instead of offering an action.
  const showFromOtherGuides = (title, guides) => {
    const names = (guides || []).map((g) => g.guideName).join(", ");
    setAlertTitle(title);
    setAlertMessage(names
      ? `From your guide${(guides || []).length === 1 ? "" : "s"}: ${names}. Open it from the Library to change it.`
      : "These come from your other guides. Open them from the Library to change them.");
    setAlertButtons([{ Title: "OK", Method: null }]);
    setAlertShow(true);
  };

  const confirm = (title, message, onConfirm) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertButtons([
      { Title: "Cancel", Method: null },
      { Title: "Delete", Method: () => { onConfirm(); refreshStats(); } },
    ]);
    setAlertShow(true);
  };

  const deleteCountry = (country) => {
    const toDelete = window.guide_obj.places.placesList.filter(p => {
      if (p.Country !== country || p.Visited === false) return false;
      return p.BucketList !== true;
    });
    if (toDelete.length === 0) return showFromOtherGuides("In your other guides", []);
    confirm(
      "Remove Country",
      `Remove all visited places in ${country}? This will delete ${toDelete.length} place${toDelete.length > 1 ? "s" : ""}.`,
      () => toDelete.forEach(p => window.guide_obj.places.deletePlace(p))
    );
  };

  const deleteBucketItem = (place) => {
    confirm(
      "Remove from Wanderlist",
      `Remove "${place.Name}" from Wanderlist? This will delete the place.`,
      () => window.guide_obj.places.deletePlace(place)
    );
  };

  const deleteVisitedCity = (city, country) => {
    const toDelete = window.guide_obj.places.placesList.filter(p => {
      if (p.City !== city || p.Country !== country || p.Visited !== true) return false;
      return p.BucketList !== true;
    });
    if (toDelete.length === 0) return showFromOtherGuides("In your other guides", []);
    confirm(
      "Remove City",
      `Remove all visited places in ${city}, ${country}? This will delete ${toDelete.length} place${toDelete.length > 1 ? "s" : ""}.`,
      () => toDelete.forEach(p => window.guide_obj.places.deletePlace(p))
    );
  };

  const deleteVisitedPlace = (place) => {
    confirm(
      "Remove Place",
      `Remove "${place.Name}" from visited places? This will delete the place.`,
      () => window.guide_obj.places.deletePlace(place)
    );
  };

  const deleteBucketCountry = (country) => {
    const toDelete = window.guide_obj.places.placesList.filter(p => {
      if (p.BucketList !== true) return false;
      return (p.Country || "Other") === country;
    });
    if (toDelete.length === 0) return showFromOtherGuides("In your other guides", []);
    confirm(
      "Remove from Wanderlist",
      `Remove all places in ${country} from Wanderlist? This will delete ${toDelete.length} place${toDelete.length > 1 ? "s" : ""}.`,
      () => toDelete.forEach(p => window.guide_obj.places.deletePlace(p))
    );
  };

  const openCityMenu = (place, isBucketList = false) => {
    if (place && place._external) return showFromOtherGuides(place.Name || place.City || "Place", place._guides);
    const menuList = [
      isBucketList
        ? {
            label: `${place.BucketList === true ? "Remove from Bucket List" : "Add to Bucket List"}`,
            method: () => {
              place.BucketList = place.BucketList === true ? false : true;
              refreshStats();
            },
            icon: null, visible: true,
          }
        : {
            label: `${place.Visited === true ? "Mark as Not Visited" : "Mark as Visited"}`,
            method: () => {
              place.Visited = place.Visited === true ? false : true;
              refreshStats();
            },
            icon: null, visible: true,
          },
      {
        label: "Show Details",
        method: () => {
          // GeoCharts owns its own HTML reader modal rather than the global
          // ShowHTMLReader redux state — nothing in the live component tree
          // watches that state (its one App.js consumer is commented out),
          // so this used to silently do nothing.
          const html = place.createPlaceDetailsHTML(place.Info, null, window.guide_obj, place.scheduledDates);
          setHtmlReaderContent(html);
          setHtmlReaderShow(true);
        },
        icon: null, visible: true,
      },
      {
        label: "Center in Map",
        method: () => {
          dispatch(slice1Actions.setShowHiddenPlace({ value: [place] }));
          dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true }));
          onHide();
        },
        icon: null, visible: true,
      },
      {
        label: "Edit Place",
        method: () => {
          setEditPlaceTarget(place);
          setEditPlaceShow(true);
        },
        icon: null, visible: true,
      },
    ];
    setCityMenuList(menuList);
    setCityMenuHeading(place.Name || place.City || "Place");
    setCityMenuShow(true);
  };

  const openTrip = (tripName, trip) => {
    if (trip && !trip.schedule) return showFromOtherGuides(tripName, trip.guides);
    if (trip && trip.schedule) tripName = trip.schedule;
    dispatch(slice1Actions.ClearAllFilters());
    dispatch(slice1Actions.setScheduleName({ value: tripName }));
    dispatch(slice1Actions.SetSchedulesFilter({ value: [{ label: tripName }] }));
    dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true }));
    dispatch(slice1Actions.setCurrentAppScreen({ value: "Map" }));
    onHide();
  };

  let onHide = props.onHide;
  
  let visited_continents = [];
  let visited_countries = [];
  let visited_cities = [];
  let data = [ ["Region", "Cities"]];
  let continents_per = 0;
  let not_visited_continents = [];
  let not_visited_countries = [];
  let places_to_visit = [];
  let not_visited_cities = [];
  let visited_cities_obj = [];   // {city, place} — for Cities Visited accordion with menu
  let visited_places = [];       // Place[] — for Places Visited accordion
  let trips_taken = [];
  let trips_planned = [];
// useEffect(() => {
  for(let i = 0; i < stats.length; i++)
  { 
    let found = false
    for(let k = 0; k < data.length; k++){
      if(data[k][0] === stats[i].Region){
        found=true;
        data[k][1] +=1;
        break;
      }
    }
    if(!found){
      data.push([stats[i].Region, 1]);
    }
  }

  if(window.guide_obj) {
    const places = allPlaces();
    for(let i = 0; i < places.length; i++){
      if(places[i].BucketList !== true) continue;
      const cityCountry = [places[i].City, places[i].Country].filter(Boolean).join(", ");
      const name = places[i].Name || cityCountry || "Unnamed";
      places_to_visit.push({ name, place: places[i] });
    }
    places_to_visit.sort((a, b) => a.name.localeCompare(b.name));

    for(let i = 0; i < places.length; i++){
      if(places[i].Visited !== false) continue;
      if(!places[i].City || !places[i].Country) continue;
      const entry = `${places[i].City}, ${places[i].Country}`;
      if(!not_visited_cities.includes(entry))
        not_visited_cities.push(entry);
    }
    not_visited_cities.sort();

    const seenCities = new Set();
    for (const p of places) {
      if (p.Visited !== true) continue;
      visited_places.push(p);
      if (p.City && p.Country) {
        const cityKey = `${p.City}, ${p.Country}`;
        if (!seenCities.has(cityKey)) {
          seenCities.add(cityKey);
          visited_cities_obj.push({ city: cityKey, place: p });
        }
      }
    }
    visited_cities_obj.sort((a, b) => a.city.localeCompare(b.city));
    visited_places.sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));

    const now = Date.now();
    const itineraries = window.guide_obj.schedule.geScheduleNames();
    for (let i = 0; i < itineraries.length; i++) {
      const name = itineraries[i].label;
      if (name === "Main") continue;
      const { startDate } = window.guide_obj.schedule.getStartEndDate(name);
      if (!startDate || !startDate.isValid) continue;
      const ms = startDate.toMillis();
      if (ms < now) {
        trips_taken.push({ name, startDate, schedule: name });
      } else {
        trips_planned.push({ name, startDate, schedule: name });
      }
    }
    // Main-schedule trips of every guide, this one included (2026-10-23):
    // split at gaps of more than 3 days; the same trip in two guides once.
    // This guide's other schedules (above) count too.
    const current = currentSource();
    mergeWanderlist([current, ...otherGuides]).trips.forEach((t) => {
      const startDate = DateTime.fromISO(t.start, { zone: "utc" });
      if (!startDate.isValid) return;
      const where = (t.cities || []).map((c) => c.city).slice(0, 3).join(", ");
      const names = [...new Set(t.guides.map((g) => g.guideName))].join(", "); // a guide and its copy often share a name
      const isCurrent = t.guides.some((g) => g.guideId === current.guideId);
      const trip = { name: where ? `${names} · ${where}` : names, startDate, schedule: isCurrent ? "Main" : null, guides: t.guides };
      (startDate.toMillis() < now ? trips_taken : trips_planned).push(trip);
    });
    trips_taken.sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis());
    trips_planned.sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis());
  }

  for(let i = 0; i < stats.length; i++){
    if(!visited_continents.includes(stats[i].Continent)) visited_continents.push(stats[i].Continent)
    if(!visited_countries.includes(stats[i].Country)) visited_countries.push(stats[i].Country)
    if(!visited_cities.includes(stats[i].City)) visited_cities.push(stats[i].City)
  }
  continents_per = ((visited_continents.length/7)*100).toFixed(2);
  not_visited_continents = [];
  not_visited_countries = [];
  for(let i = 0; i < countries.length; i++)
    if(!visited_countries.includes(countries[i]))
      not_visited_countries.push(countries[i])
  
  for(let i = 0; i < continents.length; i++)
    if(!visited_continents.includes(continents[i]))
      not_visited_continents.push(continents[i])
  visited_continents.sort();
  visited_countries.sort();
  visited_cities.sort();
  not_visited_continents.sort();
  not_visited_countries.sort();

  const _visited_continents = visited_continents.join(", ")
  const _not_visited_continents = not_visited_continents.join(", ")

  const closeModal = () => {
    onHide();
  };

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const computeChartData = () => {
    if (!window.guide_obj) return [];
    const events = window.guide_obj.schedule.events || [];
    const now = DateTime.now();

    // Year bars (2026-10-23): every year from the first to the last, empty
    // ones included, so gaps in time show; short labels ('09) when there are
    // more than 6, so they don't run into each other.
    const yearBars = (counts) => {
      const ys = Object.keys(counts).map(Number).filter(Number.isFinite);
      if (ys.length === 0) return [];
      const lo = Math.min(...ys), hi = Math.max(...ys), short = hi - lo + 1 > 6;
      const out = [];
      for (let y = lo; y <= hi; y++) out.push({ value: counts[String(y)] || 0, label: short ? `'${String(y).slice(2)}` : String(y) });
      return out;
    };
    const makeMonthBuckets = (items, getDate) => {
      const counts = {};
      for (const item of items) {
        const dt = getDate(item);
        if (!dt || !dt.isValid) continue;
        const key = `${dt.year}-${String(dt.month).padStart(2, "0")}`;
        counts[key] = (counts[key] || 0) + 1;
      }
      return Object.keys(counts).sort().map((key) => {
        const [y, mo] = key.split("-");
        return { value: counts[key], label: `${MONTHS[parseInt(mo) - 1]}\n${y}` };
      });
    };

    if (chartDataMode === "trips") {
      const scheduleMap = {};
      for (const e of events) {
        if (!e.Start) continue;
        const name = typeof e.Itinerary === "undefined" ? "Main" : e.Itinerary.replace(/%20/g, " ");
        if (name === "Main") continue;
        const dt = DateTime.fromISO(e.Start, { zone: "utc" });
        if (!dt.isValid) continue;
        if (!scheduleMap[name]) scheduleMap[name] = { name, earliest: dt };
        if (dt < scheduleMap[name].earliest) scheduleMap[name].earliest = dt;
      }
      // Every guide's Main-schedule trips too (2026-10-23), duplicates once.
      mergeWanderlist([currentSource(), ...otherGuides]).trips.forEach((t, i) => {
        const earliest = DateTime.fromISO(t.start, { zone: "utc" });
        if (earliest.isValid) scheduleMap[`__main_${i}`] = { name: t.start, earliest };
      });
      const trips = Object.values(scheduleMap).filter(t =>
        chartFilter === "visited" ? t.earliest < now : t.earliest >= now
      );
      if (chartMode === "year") {
        const counts = {};
        for (const trip of trips) {
          const key = String(trip.earliest.year);
          counts[key] = (counts[key] || 0) + 1;
        }
        return yearBars(counts);
      }
      return makeMonthBuckets(trips, t => t.earliest);
    }

    // Places mode — places scheduled in every guide's Main schedule
    // (2026-10-23; the same place on the same day counts once), visited or not.
    const visits = mergeWanderlist([currentSource(), ...otherGuides]).visits
      .filter((v) => (chartFilter === "visited" ? v.visited : !v.visited));
    const visitDate = (v) => DateTime.fromISO(v.day, { zone: "utc" });
    if (chartMode === "year") {
      const counts = {};
      for (const v of visits) {
        const dt = visitDate(v);
        if (!dt.isValid) continue;
        const key = String(dt.year);
        counts[key] = (counts[key] || 0) + 1;
      }
      return yearBars(counts);
    }
    return makeMonthBuckets(visits, visitDate);
  };

  const saveAddCity = async () => {
    const city = addCityName.trim();
    const country = addCityCountry;
    if (!city || !country) return;
    setAddCityError("");
    setAddCitySaving(true);
    try {
      const query = encodeURIComponent(`${city}, ${country}`);
      const resp = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${query}&key=${process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY}`);
      const r = await resp.json();
      if (r.status === "OK" && r.results.length > 0) {
        const loc = r.results[0].geometry.location;
        if (loc.lat === 0 && loc.lng === 0) throw new Error("zero");
        const new_place = new Place(window.guide_obj.places);
        new_place.Name = `${city}, ${country}`;
        new_place.City = city;
        new_place.Country = country;
        new_place.Visited = addCityVisited;
        new_place.BucketList = addCityBucketList;
        new_place.Lat = loc.lat;
        new_place.Longi = loc.lng;
        const findOrCreate = (name) => { const g = window.guide_obj.findGroupFromName(name); return g ? g.id : window.guide_obj.addGroup(name); };
        new_place.Group = [findOrCreate(city), findOrCreate(country)];
        window.guide_obj.places.addPlace(new_place);
        // Free-only enrichment (Wikimedia/manifest, no billed Custom Search)
        // — this is a high-frequency, unbounded add path, so it
        // deliberately skips the paid fallback that BuildMyTripWizard/
        // TripAI use for their bounded, one-shot wizard runs. Re-dispatches
        // setGuide once the lookup resolves — the setGuide below fires
        // synchronously, before this async photo fetch completes, so
        // without this a found photo would land on the place object but
        // never trigger a re-render.
        PlaceMenu.autoSetPhoto(new_place, (found) => {
          if (found) dispatch(slice1Actions.setGuide({ guide: null }));
        }, { allowPaidFallback: false });
        setAddCityShow(false);
        setAddCityName("");
        setAddCityCountry([...countries].sort()[0] || "");
        setAddCityVisited(false);
        setAddCityBucketList(false);
        setOverrideStats(window.guide_obj.getGuideStatistics());
        dispatch(slice1Actions.setGuide({ guide: null }));
      } else {
        setAddCityError(`Could not find "${city}, ${country}". Check spelling and try again.`);
      }
    } catch (_) {
      setAddCityError(`Could not find "${city}, ${country}". Check spelling and try again.`);
    } finally {
      setAddCitySaving(false);
    }
  };

 function regionClicked (region) {
    //console.log("Region", region);
    //alert('You clicked on: ' + region);
    let _region = "Unknown";
    let _continent = "Unknown";
    let citiesVisited = 0;
    for(let i = 0; i<countries_continents.length; i++){
      if(countries_continents[i].region.toUpperCase() === region.toUpperCase()){
        _region = countries_continents[i].country;
        _continent = countries_continents[i].continent;
        break;
      }
    }
    let stats = data;
    for(let i = 0; i<stats.length; i++){
      if(stats[i][0].toUpperCase() === region.toUpperCase()){
        citiesVisited = stats[i][1];
     }
    }
    setregionSelected(_region);
    setcitiesVisited(citiesVisited);
    setContinentSelected(_continent);
    //this.regionsToDisplay();
  }

  window.regionClicked = regionClicked;

  const GeoChartHTML = () => {
    const html_doc = `<html>
  <head>
    <script type="text/javascript" src="https://www.gstatic.com/charts/loader.js"></script>
    <script type="text/javascript">
    setTimeout(function() {
      google.charts.load('current', {
        'packages':['geochart'],
      });
      google.charts.setOnLoadCallback(drawRegionsMap);
    }, 300);
   
// 		async function waitForCondition(condition, maxWaitTime) {
//   const startTime = Date.now();

//   while (Date.now() - startTime < maxWaitTime) {
//     if (condition()) {
//       return true;
//     }
//     await new Promise(resolve => setTimeout(resolve, 100)); // Check every 100ms
//   }
//   return false; // Timed out
// }

// // Example usage:
// let operationCompleted = false;

// // // Simulate an asynchronous operation
// // setTimeout(() => {
// //   operationCompleted = true;
// // }, 2000);

// async function runExample() {
//   const maxWait = 3000; // Maximum wait time in milliseconds

//   const success = await waitForCondition(() => (typeof google !== "undefined"), maxWait);

//   if (success) {
//     console.log("Operation completed within the time limit.");
//       google.charts.load('current', {
//         'packages':['geochart'],
//       });
//       google.charts.setOnLoadCallback(drawRegionsMap);
//     window.regionClicked(region);
// 	 });
	
//     chart.draw(data, options);
//   } else {
//     console.log("Operation timed out");
//   }
// }

// runExample();
      function drawRegionsMap() {
        var data = google.visualization.arrayToDataTable(${regionsToDisplay()});

        var options = {colorAxis: {colors: ['#90D5FF', '#3C3CE8']}, legend: 'none', };
		
        var chart = new google.visualization.GeoChart(document.getElementById('regions_div'));

	 google.visualization.events.addListener(chart, 'regionClick', function(eventData) {
		var region = eventData.region;
		//alert('You clicked on: ' + region);
    window.regionClicked(region);
	 });
	
        chart.draw(data, options);
      }
    </script>
  </head>
  <body>
    <div id="regions_div" class="map" style="width: 100%; height: 250px;"></div>
  </body>
</html>`;
//console.log(html_doc)
    return html_doc;
  }

  const regionsToDisplay = () => {
    let data = [];
    for(let i = 0; i < stats.length; i++)
    { 
      let found = false
      for(let k = 0; k < data.length; k++){
        if(data[k][0].v.toUpperCase() === stats[i].Region.toUpperCase()){
          found=true;
          data[k][1] +=1;
          break;
        }
      }
      if(!found){
        data.push([{
          v: stats[i].Region.toUpperCase(),
          f: stats[i].Country}, 1]);
      }
    }
    const withHeader = [['Regions', 'Cities']].concat(data)
    //console.log(JSON.stringify(withHeader))
    return(JSON.stringify(withHeader))
  }
    // if (HTMLEditorModalShow)
    //   return (
    //     <HTMLEditorModal
    //       show={HTMLEditorModalShow}
    //       onHide={() => SetHTMLEditorModalShow(false)}
    //       html={HTMLEditorModalContent}
    //       callback={editCallback}
    //       callback_rt={null}
    //     />
    //   );

    // if (HTMLReaderModal)
    //   return(<HTMLReader html={HTMLContent} show={HTMLReaderModal} title={"Bucket List Notes"} onHide={() => setHTMLReaderModal(false)} />)
  const chartData = computeChartData();
  const maxVal = chartData.length > 0 ? Math.max(...chartData.map(d => d.value), 1) : 1;
  // Headroom above the tallest bar (2026-10-23): with the scale ending exactly
  // at it, its number was drawn outside the chart and cut off. Whole-number
  // steps, at most 5 sections.
  const chartSections = maxVal <= 5 ? maxVal + 1 : 5; // small counts: one step of 1 on top
  const chartTop = maxVal <= 5 ? maxVal + 1 : Math.ceil((maxVal * 1.15) / chartSections) * chartSections;
  const emptyMsg = chartDataMode === "trips"
    ? (chartFilter === "visited" ? "No taken trips found." : "No planned trips found.")
    : "No events in Main schedule yet.";

  const visitedCitiesByCountry = visited_cities_obj.reduce((acc, item) => {
    const country = item.place.Country || "Unknown";
    if (!acc[country]) acc[country] = [];
    acc[country].push(item);
    return acc;
  }, {});
  const visitedCitiesCountries = Object.keys(visitedCitiesByCountry).sort();

  const visitedPlacesByCountry = visited_places.reduce((acc, place) => {
    const country = place.Country || "Other";
    if (!acc[country]) acc[country] = [];
    acc[country].push(place);
    return acc;
  }, {});
  const visitedPlacesCountries = Object.keys(visitedPlacesByCountry).sort();

  const bucketByCountry = places_to_visit.reduce((acc, item) => {
    const country = item.place.Country || "Other";
    if (!acc[country]) acc[country] = [];
    acc[country].push(item);
    return acc;
  }, {});
  const bucketCountries = Object.keys(bucketByCountry).sort();

  const shellContent = (
          <div className={`gc-shell${props.embedded ? " gc-shell-embedded" : ""}`}>

            {/* ── Header ── */}
            <div className="gc-header">
              <div className="gc-header-icon">
                <FontAwesomeIcon icon={solid("earth-europe")} />
              </div>
              <h2 className="gc-title" id="gc-title">Wanderlist</h2>
              <button
                className="gc-add-city-btn"
                onClick={() => window.dispatchEvent(new CustomEvent("addPlacesFromGuides"))}
                title="Scan your other guides and pick which places to add to this one"
              >
                <FontAwesomeIcon icon={solid("map-location-dot")} />
                From Guides
              </button>
              <button className="gc-add-city-btn" onClick={() => { setAddCityError(""); setAddCitySaving(false); setAddCityShow(true); }}>
                <FontAwesomeIcon icon={solid("plus")} />
                Add City
              </button>
              <button className="gc-close" onClick={closeModal}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            {/* ── Body ── */}
            <div
              className="gc-body"
              onWheel={(e) => {
                e.currentTarget.scrollTop += e.deltaY;
              }}
            >

              {isSample && (
                <div style={{ margin: "0 0 10px", padding: "8px 12px", borderRadius: 8, background: "#FFF7E6", color: "#8A5A00", fontSize: 14 }}>
                  Sample Wanderlist — sign in to see your own trips.
                </div>
              )}

              {/* Map */}
              <div className="gc-map">
                <InnerHTML
                  key={stats.length}
                  className="home-resize-panel"
                  id="home-content"
                  html={GeoChartHTML()}
                />
              </div>

              {/* Region info */}
              {regionSelected !== "" ? (
                <div className="gc-region-info">
                  <span className="gc-region-chip">{continentSelected}</span>
                  <span className="gc-region-chip">{regionSelected}</span>
                  <span className="gc-region-chip">{citiesVisited} cities</span>
                </div>
              ) : null}

              {/* Activity chart */}
              <div className="gc-chart-section">
                <div className="gc-chart-header">
                  <span className="gc-chart-title">Activity</span>
                  <div className="gc-seg">
                    <button className={`gc-seg-btn${chartDataMode === "places" ? " active" : ""}`} onClick={() => setChartDataMode("places")}>Places</button>
                    <button className={`gc-seg-btn${chartDataMode === "trips" ? " active" : ""}`} onClick={() => setChartDataMode("trips")}>Trips</button>
                  </div>
                </div>
                <div className="gc-chart-filters">
                  <div className="gc-seg">
                    <button className={`gc-seg-btn${chartMode === "year" ? " active" : ""}`} onClick={() => setChartMode("year")}>Year</button>
                    <button className={`gc-seg-btn${chartMode === "month" ? " active" : ""}`} onClick={() => setChartMode("month")}>Month</button>
                  </div>
                  <div className="gc-seg">
                    <button className={`gc-seg-btn${chartFilter === "visited" ? " active" : ""}`} onClick={() => setChartFilter("visited")}>Visited</button>
                    <button className={`gc-seg-btn${chartFilter === "not-visited" ? " active" : ""}`} onClick={() => setChartFilter("not-visited")}>Not Visited</button>
                  </div>
                </div>
                {chartData.length === 0 ? (
                  <div className="gc-empty">{emptyMsg}</div>
                ) : (
                  <BarChart
                    data={chartData}
                    // Bars sized to fit the panel (~320 px) however many there are.
                    barWidth={Math.max(10, Math.min(28, Math.floor(320 / Math.max(1, chartData.length)) - 6))}
                    spacing={6}
                    labelWidth={Math.max(16, Math.min(34, Math.floor(320 / Math.max(1, chartData.length))))}
                    xAxisLabelTextStyle={{ fontSize: chartData.length > 8 ? 10 : 12, textAlign: "center" }}
                    roundedTop
                    noGradient
                    frontColor="#2563eb"
                    height={160}
                    maxValue={chartTop}
                    noOfSections={chartSections}
                    yAxisThickness={0}
                    xAxisThickness={1}
                    hideRules={false}
                    showValuesAsTopLabel
                  />
                )}
              </div>

              {/* Continent stats */}
              <div className="gc-stats">
                <div className="gc-stat-row">
                  <span className="gc-stat-label">Continents visited</span>
                  <span className="gc-stat-value">{visited_continents.length}<span className="gc-stat-total">/7</span><span className="gc-stat-pct">{continents_per}%</span></span>
                </div>
                <div className="gc-stat-sub">{_visited_continents || "—"}</div>
                <div className="gc-stat-row">
                  <span className="gc-stat-label">Not visited</span>
                  <span className="gc-stat-value">{not_visited_continents.length}<span className="gc-stat-total">/7</span><span className="gc-stat-pct">{((not_visited_continents.length/7)*100).toFixed(0)}%</span></span>
                </div>
                <div className="gc-stat-sub">{_not_visited_continents || "—"}</div>
              </div>

              {/* Trips Taken */}
              <details className="gc-acc">
                <summary className="gc-acc-header">Trips Taken<span className="gc-acc-count">{trips_taken.length}</span></summary>
                <div className="gc-acc-body">
                  {trips_taken.map((item, index) => (
                    <div key={index} className="gc-list-item clickable" onClick={() => openTrip(item.name, item)}>
                      <span className="gc-trip-date">{item.startDate.toFormat("MMM d, yyyy")}</span>
                      {" "}{item.name}
                    </div>
                  ))}
                </div>
              </details>

              {/* Trips Planned */}
              <details className="gc-acc">
                <summary className="gc-acc-header">Trips Planned<span className="gc-acc-count">{trips_planned.length}</span></summary>
                <div className="gc-acc-body">
                  {trips_planned.map((item, index) => (
                    <div key={index} className="gc-list-item clickable" onClick={() => openTrip(item.name, item)}>
                      <span className="gc-trip-date">{item.startDate.toFormat("MMM d, yyyy")}</span>
                      {" "}{item.name}
                    </div>
                  ))}
                </div>
              </details>

              {/* Territories Visited */}
              <details className="gc-acc">
                <summary className="gc-acc-header">
                  Territories Visited<span className="gc-acc-count">{visited_countries.length}/{countries.length}</span>
                </summary>
                <div className="gc-acc-body">
                  {visited_countries.map((item, index) => (
                    <div key={index} className="gc-list-item">
                      <span>{item}</span>
                      <button className="gc-del-btn" title="Delete" onClick={() => deleteCountry(item)}>
                        <FontAwesomeIcon icon={solid("trash-can")} />
                      </button>
                    </div>
                  ))}
                </div>
              </details>

              {/* Territories Not Visited */}
              <details className="gc-acc">
                <summary className="gc-acc-header">
                  Territories Not Visited<span className="gc-acc-count">{not_visited_countries.length}/{countries.length}</span>
                </summary>
                <div className="gc-acc-body">
                  {not_visited_countries.map((item, index) => (
                    <div key={index} className="gc-list-item">{item}</div>
                  ))}
                </div>
              </details>

              {/* Cities Visited */}
              <details className="gc-acc">
                <summary className="gc-acc-header">Cities Visited<span className="gc-acc-count">{visited_cities_obj.length}</span></summary>
                <div className="gc-acc-body">
                  {visitedCitiesCountries.map(country => (
                    <React.Fragment key={country}>
                      <div className="gc-list-item gc-country-header">
                        <span>{country}</span>
                        <button className="gc-del-btn" title="Delete all in country" onClick={() => deleteCountry(country)}>
                          <FontAwesomeIcon icon={solid("trash-can")} />
                        </button>
                      </div>
                      {visitedCitiesByCountry[country].map((item, idx) => (
                        <div key={idx} className="gc-list-item gc-sub-item">
                          <span className="gc-sub-label" onClick={() => openCityMenu(item.place)}>{item.place.City || item.city}</span>
                          <button className="gc-del-btn" title="Delete city" onClick={() => deleteVisitedCity(item.place.City, item.place.Country)}>
                            <FontAwesomeIcon icon={solid("trash-can")} />
                          </button>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </details>

              {/* Places Visited */}
              <details className="gc-acc">
                <summary className="gc-acc-header">Places Visited<span className="gc-acc-count">{visited_places.length}</span></summary>
                <div className="gc-acc-body">
                  {visitedPlacesCountries.map(country => (
                    <React.Fragment key={country}>
                      <div className="gc-list-item gc-country-header">
                        <span>{country}</span>
                        <button className="gc-del-btn" title="Delete all in country" onClick={() => deleteCountry(country)}>
                          <FontAwesomeIcon icon={solid("trash-can")} />
                        </button>
                      </div>
                      {visitedPlacesByCountry[country].map((place, idx) => (
                        <div key={idx} className="gc-list-item gc-sub-item">
                          <span className="gc-sub-label" onClick={() => openCityMenu(place)}>{place.Name || [place.City, place.Country].filter(Boolean).join(", ") || "Unnamed"}</span>
                          <button className="gc-del-btn" title="Delete place" onClick={() => deleteVisitedPlace(place)}>
                            <FontAwesomeIcon icon={solid("trash-can")} />
                          </button>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </details>

              {/* Bucket list */}
              <details className="gc-acc">
                <summary className="gc-acc-header">Bucket List<span className="gc-acc-count">{places_to_visit.length}</span></summary>
                <div className="gc-acc-body">
                  {bucketCountries.map(country => (
                    <React.Fragment key={country}>
                      <div className="gc-list-item gc-country-header">
                        <span>{country}</span>
                        <button className="gc-del-btn" title="Delete all in country" onClick={() => deleteBucketCountry(country)}>
                          <FontAwesomeIcon icon={solid("trash-can")} />
                        </button>
                      </div>
                      {bucketByCountry[country].map((item, idx) => (
                        <div key={idx} className="gc-list-item gc-sub-item">
                          <span className="gc-sub-label" onClick={() => openCityMenu(item.place, true)}>{item.name}</span>
                          <button className="gc-del-btn" title="Delete" onClick={() => deleteBucketItem(item.place)}>
                            <FontAwesomeIcon icon={solid("trash-can")} />
                          </button>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </details>

            </div>
          </div>
  );

  return (
    <div className={props.embedded ? "gc-embedded" : "modal"}>
      {props.embedded ? (
        shellContent
      ) : (
        <Modal
          {...props}
          animation={false}
          dialogClassName="gc-dialog"
          aria-labelledby="gc-title"
          centered
          scrollable={false}
          onHide={closeModal}
        >
          <Modal.Body style={{ padding: 0 }}>{shellContent}</Modal.Body>
        </Modal>
      )}

      <AlertModal
        show={alertShow}
        onHide={() => setAlertShow(false)}
        AlertModalTitle={alertTitle}
        AlertModalMessage={alertMessage}
        AlertModalButtons={alertButtons}
      />
      <MenuModal
        show={cityMenuShow}
        onHide={() => setCityMenuShow(false)}
        menu_modal_list={cityMenuList}
        menu_modal_heading={cityMenuHeading}
      />
      {editPlaceShow && editPlaceTarget ? (
        <PlaceEditModal
          show={editPlaceShow}
          onHide={() => setEditPlaceShow(false)}
          place={editPlaceTarget}
          user_info={user_info}
          mode={"Edit"}
          groups={window.guide_obj ? window.guide_obj.groupNamesFromIDs(editPlaceTarget.Group) : ""}
          category={window.guide_obj ? window.guide_obj.categoryNamesFromIDs(editPlaceTarget.Category) : ""}
          update_display={() => refreshStats()}
        />
      ) : null}
      <HTMLReader
        html={htmlReaderContent}
        show={htmlReaderShow}
        title="Place Details"
        onHide={() => setHtmlReaderShow(false)}
      />

      {/* Add City dialog */}
      <Modal show={addCityShow} onHide={() => setAddCityShow(false)} centered dialogClassName="gc-add-dialog">
        <Modal.Body style={{ padding: 0 }}>
          <div className="gc-add-shell">
            <div className="gc-add-header">
              <span className="gc-add-title">Add City</span>
              <button className="gc-close" onClick={() => setAddCityShow(false)}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>
            <div className="gc-add-body">
              <div className="gc-field">
                <label className="gc-label">Country</label>
                <select
                  className="gc-select"
                  value={addCityCountry}
                  onChange={(e) => {
                    // Blanking the city on an actual country change avoids
                    // leaving a stale city from the old country — city
                    // choices are scoped to a country.
                    setAddCityCountry(e.target.value);
                    setAddCityName("");
                    setAddCityMode("select");
                  }}
                >
                  {[...countries].sort().map((c, i) => (
                    <option key={i} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="gc-field">
                <label className="gc-label">City</label>
                {(() => {
                  const cityList = (citiesByCountry[addCityCountry] || []).slice().sort((a, b) => b.popularity - a.popularity);
                  if (cityList.length > 0 && addCityMode === "select") {
                    return (
                      <select
                        className="gc-select"
                        value={addCityName}
                        onChange={(e) => {
                          if (e.target.value === "__other__") {
                            setAddCityMode("type");
                            setAddCityName("");
                          } else {
                            setAddCityName(e.target.value);
                          }
                        }}
                      >
                        <option value="" disabled>Select a city…</option>
                        {cityList.map((c) => (
                          <option key={c.city} value={c.city}>{c.city}</option>
                        ))}
                        <option value="__other__">Type a different city…</option>
                      </select>
                    );
                  }
                  // Free-text fallback — for a country with no curated cities,
                  // or the dropdown's own "type a different city" escape
                  // hatch. No geocode validation: this field also accepts
                  // non-geocodable entries like a neighborhood or region.
                  return (
                    <input
                      className="gc-input"
                      type="text"
                      value={addCityName}
                      onChange={(e) => setAddCityName(e.target.value)}
                      onKeyPress={(e) => { if (e.charCode === 13) saveAddCity(); }}
                      autoFocus
                    />
                  );
                })()}
              </div>
              <div className="gc-toggle-row">
                <span className="gc-label">Visited</span>
                <span
                  className={`gc-toggle${addCityVisited ? " on" : ""}`}
                  onClick={() => setAddCityVisited(v => !v)}
                />
              </div>
              <div className="gc-toggle-row">
                <span className="gc-label">Add to Bucket List</span>
                <span
                  className={`gc-toggle${addCityBucketList ? " on" : ""}`}
                  onClick={() => setAddCityBucketList(v => !v)}
                />
              </div>
            </div>
            {addCityError && <div className="gc-error-note" style={{ margin: "0 16px 8px" }}>{addCityError}</div>}
            <div className="gc-add-footer">
              <button className="gc-btn-cancel" onClick={() => setAddCityShow(false)} disabled={addCitySaving}>Cancel</button>
              <button className="gc-btn-save" onClick={saveAddCity} disabled={addCitySaving}>{addCitySaving ? "Adding…" : "Add"}</button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};
export default GeoCharts;
